import {
  EnumUserAccessOperatorsStatus,
  UserFindOneByEmailQuery,
  useUserFindOneByEmailLazyQuery,
} from "gql/generated.ts";
import { useOnboardingContext } from "onboarding/OnboardingContext";
import { useNavigate } from "react-router-dom";

export const useUserForSignup = () => {
  const {
    organization,
    operatorId,
    emailAddress,
    invitedFrom,
    onScreenChange,
    onUserFind,
  } = useOnboardingContext();
  const navigate = useNavigate();

  /**
   * Query to check if user exists in our DB
   * This will determine the next stage of the onboarding flow.
   * See notes below for a more detailed breakdown
   */
  const [loadUser, { loading: loadingUser }] = useUserFindOneByEmailLazyQuery({
    variables: {
      email: emailAddress,
    },
  });

  /* Handler to check what screen to navigate to, during a cold signup */
  const handleColdSignup = (
    user?: UserFindOneByEmailQuery["userFindOneByEmail"],
  ) => {
    /* Case 1: Organization cold signup is enabled. Go to "welcome" screen */
    if (organization?.onboardingMetadata.allowColdSignup) {
      onScreenChange("welcome");
    } else {
      /* Case 2: Organization has cold signup disabled */
      if (organization) {
        onScreenChange("coming-soon");
      } else {
        /* Case 3: Ice-cold signup */

        /* Case 3a: If user exists already, navigate to login page */
        if (user) {
          navigate(`/app?email=${user.email.toLowerCase()}`);
        } else {
          /* Case 3b: User doesn't exist or hasn't been enrolled, navigate to "Get in Touch" screen */
          onScreenChange("get-in-touch");
        }
      }
    }
    return;
  };

  const findUserAndNavigate = async () =>
    await loadUser()
      .then(async (res) => {
        const user = res.data?.userFindOneByEmail;
        onUserFind?.(user);

        /**
         * Case 1: User exists and is part of the organization
         * Regardless of the `allowColdSignup` boolean, enable user to continue onboarding
         */
        if (user && organization) {
          /**
           * Check for the user's access object. If inviting for an operator,
           * it will exist in their `access.operators` array. If not, onboarding
           * is happening for a DMO user. Check their `access.organizations` array
           */
          const userAccess = operatorId
            ? user.access.operators.find((org) => org?.entityId === operatorId)
            : user.access.organizations.find(
                (org) => org?.entityId === organization?._id,
              );

          if (!userAccess) {
            // If we can't find the user's access object (likely because they came via a ?organizationId=... URL param only)
            // then we should show the welcome screen
            onScreenChange("welcome");
            return;
          }

          if (userAccess.status === EnumUserAccessOperatorsStatus.Invited) {
            if (invitedFrom === "OPERATOR") {
              /* Case 1a: If user is being invited to an operator, go to create screen */
              onScreenChange("create");
            } else {
              /* Case 1b: If user does NOT have claimed operators, go to welcome screen */
              onScreenChange("welcome");
            }
          } else if (
            /* Case 1c: If user has already completed onboarding, go to login screen */
            userAccess.status === EnumUserAccessOperatorsStatus.Accepted
          ) {
            navigate(`/app?email=${user.email.toLowerCase()}`);
          }
        } else {
          /* Case 2: User does not exist, or user does not have access to organization */

          /* Case 2a: User is invited from an operator, so go to "create" screen */
          if (invitedFrom === "OPERATOR") {
            onScreenChange("create");
          } else {
            /* Case 2b: Cold signup, no invites and user doesn't exist */
            handleColdSignup(user);
            return;
          }
        }
      })
      .catch(() => {
        throw new Error("Error finding user.");
      });

  return {
    handleColdSignup,
    findUserAndNavigate,
    loadingUser,
  };
};
